import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Typography, Box, IconButton } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectInventorySelections } from '@/redux/selectors/orderPlacementSelectors';
import { removeInventorySelection } from '@/redux/slices/orderPlacementSlice';
import { FileTypeDisplay } from '@/exports/columnCommonComponents';
import { selectAllLanguages } from '@/redux/selectors/constantsSelectors';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import useFileType from '@/components/_hooks/useFileType';

type FileSelectContentsProps = {
  noVideo?: boolean;
  fullWidth?: boolean;
}

const FileSelectContents: React.FC<FileSelectContentsProps> = ({ noVideo = false, fullWidth = false }) => {
  const dispatch = useAppDispatch();
  const selected = useAppSelector(selectInventorySelections);
  const languages = useAppSelector(selectAllLanguages);
  const getFileGroup = useFileType();

  const groupedBySection = useMemo(() => {
    return selected.reduce((acc, file) => {
      const section = getFileGroup(file.file_type) || 'Uncategorized';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(file);
      return acc;
    }, {} as Record<string, typeof selected>)
  }, [selected, getFileGroup]);

  const sections = Object.keys(groupedBySection);

  const removeFile = (fileId: number) => {
    const file = selected.find(({ id }) => id === fileId);

    if (file) {
      dispatch(removeInventorySelection(file));
    }
  };

  const getFileLanguage = (file: InventoryFileEntity) => {
    const match = languages.find((lang) =>
      file.rally_asset_tags.some((tag) => lang.label?.toLowerCase() === tag.toLowerCase())
    );
    return match?.label || 'Unknown';
  }

  const renderGroupedFiles = () => {
    return sections.map((section) => (
      <Box sx={{ mb: 2 }} key={section}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontWeight: 600, fontSize: '1rem' }}>
          <GroupedFileType chip={false} type={section} sx={{ mb: 1.5 }} />
          ({groupedBySection[section].length})
        </Box>
        {groupedBySection[section].map((file) => {
          const fileName = file.name?.split('/').pop() || file.label;

          return (
            <SelectedFileRow key={file.id}>
              <SelectedFileMeta>
                <SelectedFileLanguage>{getFileLanguage(file)}</SelectedFileLanguage>
                <SelectedFileName>{fileName}</SelectedFileName>
              </SelectedFileMeta>
              <RemoveFileButton size="small" onClick={() => removeFile(file.id)}>
                <PolarisIcon icon="close" width={14} height={14} />
              </RemoveFileButton>
            </SelectedFileRow>
          );
        })}
      </Box>
    ));
  }

  return (
    <ContentWrapper fullWidth={fullWidth}>
      {!selected.length || (!groupedBySection['video']?.length && !noVideo) ? (
        <EmptyState>
          <PolarisIcon icon="home_storage" width={40} height={40} />
          <Typography variant="body2">{!selected.length ? 'Select media files to start' : 'No video file selected' }</Typography>
        </EmptyState>
      ) : null}
      {renderGroupedFiles()}
    </ContentWrapper>
  );
};

export default FileSelectContents;

const ContentWrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '283px')};
  padding: ${({ theme }) => theme.spacing(1.5)};
  max-height: 520px;
  overflow-y: auto;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0 0 ${({ theme }) => theme.shape.borderRadius}px ${({ theme }) => theme.shape.borderRadius}px;
  transition: border-color 150ms ease, box-shadow 150ms ease;
`;

const EmptyState = styled.div`
  min-height: 96px;
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.text.secondary};
  border: 1px dashed ${({ theme }) => theme.palette.divider};
`;

const SelectedFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.25, 2)};
  margin: ${({ theme }) => theme.spacing(1, 0)};
  background: ${({ theme }) => theme?.palette.background.default + '6a'};

  &:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;

const SelectedFileMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.25)};
`;

const SelectedFileLanguage = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedFileName = styled.div`
  text-transform: capitalize;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.85rem;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const GroupedFileType = styled(FileTypeDisplay)`
  display: inline-flex;

  svg {
      color: ${({ theme }) => theme.palette.text.secondary};
      width: 14px;
      height: 14px;
      box-sizing: content-box;
  }

  span {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 1rem;
  }
`;

const RemoveFileButton = styled(IconButton)`
  color: ${({ theme }) => theme.palette.text.primary};
`;
